"use client";
import { useEffect, useLayoutEffect, useRef, useState, useId } from "react";
import { SaveButtonSpinner } from "@/components/save-button-spinner";
import { Plus } from "lucide-react";
import type { SchemaField, SchemaPage } from "@/lib/docs/nexum/catalog";

function Field({
  field,
  source,
  depth,
}: {
  field: SchemaField;
  source: string;
  depth: number;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const [visited, setVisited] = useState(false);
  const [height, setHeight] = useState(0);
  const childrenRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = childrenRef.current;
    if (!element) return;
    const measure = () => setHeight(element.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [visited]);
  const expandable = field.expandable && depth < 12;
  const content = (
    <>
      <span className="api-field-heading">
        <code>{field.name}</code>
        <span className="api-field-type">{field.type}</span>
        <span className={field.required ? "api-required" : "api-optional"}>
          {field.required ? "required" : "optional"}
        </span>
        {expandable && (
          <span className="api-field-action" aria-hidden="true">
            <span className="api-field-action-label">
              <span>{open ? "Hide properties" : "Show properties"}</span>
            </span>
            <Plus
              size={14}
              strokeWidth={2.5}
              className="api-field-action-icon"
            />
          </span>
        )}
      </span>
      {field.description && (
        <span className="api-field-description">{field.description}</span>
      )}
    </>
  );
  return (
    <div
      className="api-field api-schema-field-enter"
      data-expanded={open}
      data-expandable={expandable}
    >
      {expandable ? (
        <button
          type="button"
          className="api-field-toggle"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => {
            setVisited(true);
            setOpen(!open);
          }}
        >
          {content}
        </button>
      ) : (
        <div className="api-field-toggle">{content}</div>
      )}
      {expandable && (
        <div
          id={id}
          className="api-field-collapse"
          style={{ height: open ? height : 0 }}
          aria-hidden={!open}
          inert={!open}
        >
          {visited && (
            <div ref={childrenRef}>
              <div className="api-field-children">
                <ApiSchema
                  source={source}
                  pointer={field.pointer}
                  depth={depth + 1}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
export function ApiSchema({
  source,
  pointer,
  initial,
  depth = 0,
}: {
  source: string;
  pointer: string;
  initial?: SchemaPage;
  depth?: number;
}) {
  const [page, setPage] = useState(initial);
  const [offset, setOffset] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (initial && offset === 0) return;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    fetch(
      `/api/docs/nexum/schema?${new URLSearchParams({ source, pointer, offset: String(offset) })}`,
      { signal: controller.signal },
    )
      .then((response) => {
        if (!response.ok) throw new Error();
        return response.json() as Promise<SchemaPage>;
      })
      .then((next) => {
        if (!controller.signal.aborted)
          setPage((previous) => ({
            ...next,
            fields: offset
              ? [...(previous?.fields || []), ...next.fields]
              : next.fields,
          }));
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [source, pointer, initial, offset, attempt]);
  return (
    <div className="api-schema">
      {page?.fields.map((field) => (
        <Field
          key={field.pointer}
          field={field}
          source={source}
          depth={depth}
        />
      ))}
      <div
        aria-hidden={!loading}
        className={`grid transition-[grid-template-rows,opacity] duration-200 ease-in-out motion-reduce:transition-none ${loading ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="min-h-0 overflow-hidden">
          <p
            role={loading ? "status" : undefined}
            className="flex items-center py-3 text-xs text-muted"
          >
            <SaveButtonSpinner show={loading} size="sm" margin="right" />
            Loading fields…
          </p>
        </div>
      </div>
      {!loading && !error && page?.next != null && (
        <button
          className="py-3 text-xs text-brand-primary dark:text-brand-accent"
          onClick={() => setOffset(page.next!)}
        >
          Show more fields
        </button>
      )}
      {page?.fields.length === 0 && (
        <p className="py-3 text-xs text-muted">No named fields defined.</p>
      )}
      {error && (
        <p role="alert" className="py-3 text-xs text-muted">
          Couldn’t load fields.{" "}
          <button className="underline" onClick={() => setAttempt(attempt + 1)}>
            Retry
          </button>
        </p>
      )}
    </div>
  );
}
